// Parse the SPA's own URL on bootstrap into a typed init object.
//
// Two input shapes are accepted:
//   1. Canonical params produced by cos_parent.ts (Standalone-Redirect
//      von der CMS-Seite): ?type=Cycle&malo=…&pinNo=…
//   2. Direct cos_-prefixed parent params (so opening the SPA URL
//      manually with ?cos_malo=…&cos_pinNo=… also works during dev):
//      ?cos_malo=…&cos_pinNo=…
//
// Lookups against the cos_ form are case-insensitive on the key part after
// the prefix, mirroring the legacy onParentEnvObjectReceived behaviour.

export interface InitParams {
  /** Module variant — for /meterreading: `Common` (Benutzerkonto + PIN)
   *  or `CommonIntern` (Benutzerkonto + Postleitzahl); for /landpages:
   *  `DoiOptin`, `DoiOptout`, `Bfsg`. */
  type?: string;
  /** Optional sub-variant flag (legacy, currently unused). */
  sub?: string;

  // --- meterreading prefill ---
  serialNo?: string;
  malo?: string;
  pinNo?: string;
  postalCode?: string;
  /** "1" forces a specific backend (Pfalzgas-only legacy flag, kept for compat). */
  forceSys?: string;

  // --- landpages / DOI ---
  lpType?: string;
  doiGuid?: string;
  doiEmail?: string;
  doiType?: string;
  /** "1" → confirm immediately on landing. */
  doiDirectConfirm?: string;
}

/**
 * Maps the lowercased key (with `cos_` prefix already stripped) to its
 * canonical camelCase name in InitParams. Single source of truth used by
 * both this parser and the parent-side `buildStandaloneUrl()` so the two
 * never disagree on naming.
 */
export const CANONICAL_FIELD_MAP: Record<string, keyof InitParams> = {
  type: "type",
  sub: "sub",
  serialno: "serialNo",
  malo: "malo",
  pinno: "pinNo",
  postalcode: "postalCode",
  forcesys: "forceSys",
  lptype: "lpType",
  doiguid: "doiGuid",
  doiemail: "doiEmail",
  doitype: "doiType",
  doidirectconfirm: "doiDirectConfirm",
};

export function readInitParams(search: string = window.location.search): InitParams {
  const p = new URLSearchParams(search);
  const out: InitParams = {};

  for (const [rawKey, value] of p.entries()) {
    const stripped = rawKey.toLowerCase().replace(/^cos_/, "");
    const canonical = CANONICAL_FIELD_MAP[stripped];
    if (!canonical) continue;
    // Last write wins. The canonical (unprefixed) form, if present, comes
    // last in our iteration only by chance — so to be deterministic we
    // prefer the canonical form when both are present.
    if (out[canonical] !== undefined && rawKey.toLowerCase().startsWith("cos_")) {
      continue;
    }
    out[canonical] = value;
  }
  return out;
}
